import create from 'zustand';
import { useEffect } from 'react';
import { BondTransactionActionEnum } from 'global/Securities/types/api/bond';
import OrderPlacedModal from 'global/TradingModal/OrderPlacedModal';

import { useRouter } from 'next/router';
import If from 'global/ErrorHandlers/If';
import { ORDER_TYPE } from 'constants/trading';
import ModalBondsBuy from './components/ModalBondsBuy';
import ModalBondBlockers from './components/ModalBondBlockers';
import ModalBondConfirm from './components/ModalBondConfirm';
import ModalBondSell from './components/ModalBondSell';
import ModalBondTnC from './components/ModalBondTnC';
import ModalBondOrder from './components/ModalBondOrder';
import ModalProductSelection from './components/ModalProductSelection';
import ModalBondHistory from './components/ModalBondHistory';
import ModalBondInformation from './components/ModalBondInformation';
import ModalBondTax from './components/ModalBondTax';

import { useBondsModalStore } from './stores/useBondsModalStore';
import useBondError from './stores/useBondError';

import { BondsModalTypeEnum } from './constants';
import { ModalRoot } from './styled';

const useBondErrorStore = create(useBondError);

export default function ModalBonds() {
  const router = useRouter();

  const { blockerType, resetErrorCode, error } = useBondErrorStore((s) => ({
    blockerType: s.blockerType,
    resetErrorCode: s.resetErrorCode,
    error: s.errorCode,
  }));
  const {
    modalType,
    closeModal,
    transactionType,
    openBlockerModal,
    closeTransactionModal,
  } = useBondsModalStore((s) => ({
    modalType: s.modalType,
    closeModal: s.closeModal,
    transactionType: s.transactionType,
    openBlockerModal: s.openBlockerModal,
    closeTransactionModal: s.closeTransactionModal,
  }));

  const handleOrderPlacedClose = () => {
    closeModal();
    router.push(`/securities/order/?t=${Date.now()}`);
  };

  const handleBlockerClose = () => {
    resetErrorCode();
    closeModal();
  };

  useEffect(() => {
    if (!error) {
      return;
    }

    if (modalType === BondsModalTypeEnum.BLOCKERS) {
      return;
    }

    // if only alert, we immidately reset the error code
    if (!blockerType) {
      resetErrorCode();
      return;
    }

    openBlockerModal(blockerType);
  }, [blockerType, modalType, error]);

  return (
    <>
      <ModalRoot
        visible={modalType === BondsModalTypeEnum.BLOCKERS}
        onCancel={handleBlockerClose}
      >
        <ModalBondBlockers onClose={handleBlockerClose} />
      </ModalRoot>
      <ModalRoot
        visible={modalType === BondsModalTypeEnum.CONFIRMATION}
        onCancel={closeModal}
      >
        <ModalBondConfirm />
      </ModalRoot>
      <If condition={modalType === BondsModalTypeEnum.TRANSACTION}>
        <If condition={transactionType === BondTransactionActionEnum.BUY}>
          <ModalRoot
            visible={
              modalType === BondsModalTypeEnum.TRANSACTION &&
              transactionType === BondTransactionActionEnum.BUY
            }
            onCancel={closeTransactionModal}
          >
            <ModalBondsBuy />
          </ModalRoot>
        </If>
        <If condition={transactionType === BondTransactionActionEnum.SELL}>
          <ModalRoot
            visible={
              modalType === BondsModalTypeEnum.TRANSACTION &&
              transactionType === BondTransactionActionEnum.SELL
            }
            onCancel={closeTransactionModal}
          >
            <ModalBondSell />
          </ModalRoot>
        </If>
      </If>
      <ModalBondTnC
        visible={modalType === BondsModalTypeEnum.TNC}
        onCancel={closeModal}
      />
      <ModalRoot
        visible={modalType === BondsModalTypeEnum.ORDER}
        onCancel={closeModal}
      >
        <ModalBondOrder />
      </ModalRoot>
      <ModalRoot
        visible={modalType === BondsModalTypeEnum.HISTORY}
        onCancel={closeModal}
      >
        <ModalBondHistory />
      </ModalRoot>
      <ModalRoot
        visible={modalType === BondsModalTypeEnum.ASSETS}
        onCancel={closeModal}
      >
        <ModalProductSelection />
      </ModalRoot>
      <OrderPlacedModal
        visible={modalType === BondsModalTypeEnum.ORDER_PLACED}
        onSubmit={handleOrderPlacedClose}
        orderType={ORDER_TYPE.BOND}
      />
      <ModalBondInformation />
      <ModalBondTax />
    </>
  );
}
